import { diffLines } from "diff";
import { type FC, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

const CONTEXT_LINES = 3;

interface AttachmentDiffViewProps {
  savedBody: string;
  editedBody: string;
}

interface DiffLine {
  type: "added" | "removed" | "unchanged";
  text: string;
}

function computeDiffLines(savedBody: string, editedBody: string): DiffLine[] {
  const changes = diffLines(savedBody, editedBody);
  const lines: DiffLine[] = [];

  for (const change of changes) {
    // Split into individual lines, removing trailing empty string from final newline
    const changeLines = change.value.replace(/\n$/, "").split("\n");
    const type = change.added
      ? "added"
      : change.removed
        ? "removed"
        : "unchanged";
    for (const line of changeLines) {
      lines.push({ type, text: line });
    }
  }

  return lines;
}

export function computeDiffStats(
  savedBody: string,
  editedBody: string,
): { added: number; removed: number } {
  const lines = computeDiffLines(savedBody, editedBody);
  let added = 0;
  let removed = 0;
  for (const line of lines) {
    if (line.type === "added") added++;
    if (line.type === "removed") removed++;
  }
  return { added, removed };
}

/** Chunks of lines grouped for display, with collapsible unchanged sections. */
type Chunk =
  | { type: "changed"; lines: DiffLine[] }
  | { type: "collapsed"; lines: DiffLine[]; index: number };

function buildChunks(diffLines: readonly DiffLine[]): Chunk[] {
  const chunks: Chunk[] = [];
  let currentUnchanged: DiffLine[] = [];
  let collapseIndex = 0;

  const flushUnchanged = () => {
    if (currentUnchanged.length === 0) return;

    if (currentUnchanged.length <= CONTEXT_LINES * 2 + 1) {
      // Too few to collapse — show all as changed chunk
      chunks.push({ type: "changed", lines: [...currentUnchanged] });
    } else {
      // Show first N context lines, collapsed middle, last N context lines
      const top = currentUnchanged.slice(0, CONTEXT_LINES);
      const middle = currentUnchanged.slice(
        CONTEXT_LINES,
        currentUnchanged.length - CONTEXT_LINES,
      );
      const bottom = currentUnchanged.slice(
        currentUnchanged.length - CONTEXT_LINES,
      );

      if (chunks.length > 0) {
        // Only show top context if there was a previous change
        chunks.push({ type: "changed", lines: top });
      }
      chunks.push({
        type: "collapsed",
        lines: middle,
        index: collapseIndex++,
      });
      chunks.push({ type: "changed", lines: bottom });
    }

    currentUnchanged = [];
  };

  for (const line of diffLines) {
    if (line.type === "unchanged") {
      currentUnchanged.push(line);
    } else {
      flushUnchanged();
      // Append to the last changed chunk or create a new one
      const lastChunk = chunks[chunks.length - 1];
      if (lastChunk && lastChunk.type === "changed") {
        lastChunk.lines.push(line);
      } else {
        chunks.push({ type: "changed", lines: [line] });
      }
    }
  }

  flushUnchanged();
  return chunks;
}

export const AttachmentDiffView: FC<AttachmentDiffViewProps> = ({
  savedBody,
  editedBody,
}) => {
  const lines = useMemo(
    () => computeDiffLines(savedBody, editedBody),
    [savedBody, editedBody],
  );

  const chunks = useMemo(() => buildChunks(lines), [lines]);

  const [expandedSections, setExpandedSections] = useState<
    Record<number, boolean>
  >({});

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (savedBody === editedBody) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        No changes
      </div>
    );
  }

  return (
    <div className="relative min-h-0 flex-1 overflow-y-auto">
      <pre className="text-sm leading-6">
        {chunks.map((chunk, chunkIdx) => {
          if (chunk.type === "collapsed") {
            if (expandedSections[chunk.index]) {
              return chunk.lines.map((line, lineIdx) => (
                <DiffLineRow
                  key={`${chunkIdx}-${lineIdx}`}
                  type={line.type}
                  text={line.text}
                />
              ));
            }
            return (
              <button
                key={`collapse-${chunk.index}`}
                type="button"
                className="w-full cursor-pointer border-y border-border-light bg-gray-50 px-4 py-1 text-center text-xs text-muted-foreground hover:bg-gray-100 hover:text-gray-700"
                onClick={() => toggleSection(chunk.index)}
              >
                {chunk.lines.length} unchanged lines
              </button>
            );
          }

          return chunk.lines.map((line, lineIdx) => (
            <DiffLineRow
              key={`${chunkIdx}-${lineIdx}`}
              type={line.type}
              text={line.text}
            />
          ));
        })}
      </pre>
    </div>
  );
};

const DiffLineRow: FC<{ type: DiffLine["type"]; text: string }> = ({
  type,
  text,
}) => {
  return (
    <div
      className={twMerge(
        "px-4 py-0.5 break-words whitespace-pre-wrap",
        type === "added" && "bg-green-50 text-green-800",
        type === "removed" && "bg-red-50 text-red-800",
      )}
    >
      <span className="mr-3 inline-block w-4 text-right opacity-50 select-none">
        {type === "added" ? "+" : type === "removed" ? "−" : " "}
      </span>
      {text || " "}
    </div>
  );
};
